import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { HazardIdEnum } from "../hazardIdEnum";
import httpClient from "../../../lib/httpClient";
import { AxiosError } from "axios";
import { verbatimLevelMarker } from "../hazardUtils";

type HazardValuesHookArgs = {
  hazardsList: Hazard[];
  lat: number;
  lng: number;
};

type HazardHookResult = {
  hazardValues: HazardWithData[];
  loading: boolean;
  error: string | null;
};

const deepEqual = (arr1: Array<any>, arr2: Array<any>): boolean => {
  return JSON.stringify(arr1) === JSON.stringify(arr2);
};

const useGetHazardsValues = ({ hazardsList = [], lat, lng }: HazardValuesHookArgs): HazardHookResult => {
  const { t } = useTranslation();

  const [hazardValues, setHazardValues] = useState<HazardWithData[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [prevIds, setPrevIds] = useState<Hazard[]>([]);
  const [prevLat, setPrevLat] = useState<Number>();
  const [prevLng, setPrevLng] = useState<Number>();

  const getAPIData = useCallback(async () => {
    const makeHazardWithData = async (
      currHazard: Hazard,
      responseData: HazardValueResponse[],
      lat: number,
      lng: number,
    ): Promise<HazardWithData> => {
      if (currHazard.evaluateByFrontend) {
        let currHazardWithValue: HazardValueResponse;
        try {
          currHazardWithValue = await currHazard.evaluateByFrontend(lat, lng);
        } catch (error) {
          console.error("Error in evaluateByFrontend for hazard", currHazard.cysId, error);
          throw error;
        }
        const ret: HazardWithData = {
          ...currHazard,
          ...currHazardWithValue,
          level: formattedLevelToDisplay(currHazardWithValue.level),
        };

        return ret;
      } else {
        const currHazardWithValue: HazardValueResponse | undefined = responseData.find(
          (hazardWithValue: HazardValueResponse) => hazardWithValue.cysId === currHazard.cysId,
        );
        if (!currHazardWithValue) {
          throw new Error(`Hazard ${currHazard.cysId} not found in response`);
        }
        const ret: HazardWithData = {
          ...currHazard,
          ...currHazardWithValue,
          level: formattedLevelToDisplay(currHazardWithValue.level),
        };

        return ret;
      }
    };

    setLoading(true);
    try {
      const hazardListForBackend = hazardsList.filter((hazard) => !hazard.evaluateByFrontend);
      const hazardsIdsForBackend = hazardListForBackend.map((hazard) => hazard.cysId as HazardIdEnum);

      const response = await httpClient().post("/proxy/hazards", {
        hazardIds: hazardsIdsForBackend,
        latitude: lat,
        longitude: lng,
      });
      const responseData: HazardValueResponse[] = response.data;

      const hazardValuesCompleted: HazardWithData[] = await Promise.all(
        hazardsList.map(
          async (currHazard: Hazard) => await makeHazardWithData(currHazard, responseData, lat, lng),
        ),
      );

      setHazardValues(hazardValuesCompleted);
    } catch (error) {
      const axiosError = error as AxiosError;
      setError(axiosError.message);
    }
    setLoading(false);
  }, [hazardsList, lat, lng, t]);

  useEffect(() => {
    if (deepEqual(hazardsList, prevIds) && lat === prevLat && lng === prevLng) return;

    if (hazardsList.length && lat && lng) {
      getAPIData();
    }

    setPrevIds(hazardsList);
    setPrevLat(lat);
    setPrevLng(lng);
  }, [prevIds, prevLat, prevLng, hazardsList, lat, lng, getAPIData]);

  const formattedLevelToDisplay = (receivedLevel: string) => {
    if (receivedLevel.startsWith(verbatimLevelMarker)) {
      // We do not translate verbatim level names, for example exotic crop names, like "Potatoes"
      const verbatimLevel = receivedLevel.split("=")[1];
      return verbatimLevel;
    }
    return t(`levels.${receivedLevel}`);
  };

  return { hazardValues, error, loading };
};

export default useGetHazardsValues;
